import React, { useState, useEffect, useCallback } from 'react';
import { TrInputNumber as InputNumber } from '../components/TrInputNumber';
import {
  Card,
  Row,
  Col,
  Table,
  Input,
  Select,
  Button,
  Space,
  message,
  Checkbox,
  DatePicker,
  Tooltip,
  Popconfirm,
  Switch,
  Drawer
} from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, SearchOutlined, CarOutlined } from '@ant-design/icons';
import { api } from '../services/api';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useCurrency } from '../contexts/CurrencyContext';
import { fetchProducts, fetchProductVariants, type AttributeFilterParam } from '../services/productService';
import { fetchCategoryAttributes, AttributeDataType, type CategoryAttributeDto } from '../services/categoryAttributeService';
import { fetchProductBrands, type ProductBrandDto } from '../services/productBrandService';
import { fetchBrands, fetchModels, type BrandDto, type ModelDto } from '../services/vehicleService';

interface Category {
  id: number;
  categoryName: string;
  parentId?: number | null;
}

interface Product {
  id: number;
  productId?: number; // Varyant için ana ürün ID'si
  productCode: string;
  productName: string;
  sku?: string; // Varyant için SKU
  barcode?: string;
  categoryId: number;
  categoryName: string;
  productBrandId?: number | null;
  isVariant: boolean; // Varyant mı ana ürün mü?
  variantCombination?: string; // Varyant kombinasyonu
  currentRetailPrice?: number;
  currentWholesalePrice?: number;
  currentPurchasePrice?: number;
}

interface SelectedProduct {
  id: number;
  productId?: number; // Varyant için ana ürün ID'si
  productVariantId?: number; // Varyant ID'si (varsa)
  productCode: string;
  productName: string;
  purchasePrice: number;
  currentRetailPrice: number;
  currentWholesalePrice: number;
  newRetailPrice?: number;
  newWholesalePrice?: number;
  validFrom: Dayjs;
  validTo?: Dayjs;
  isIndefinite: boolean;
  isVariant: boolean;
}

const PriceBulkUpdate: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Filtreleme
  const [searchText, setSearchText] = useState('');
  const [searchBarcode, setSearchBarcode] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedProductBrand, setSelectedProductBrand] = useState<number | undefined>();
  const [productBrands, setProductBrands] = useState<ProductBrandDto[]>([]);
  const [categoryAttributeFilters, setCategoryAttributeFilters] = useState<CategoryAttributeDto[]>([]);
  const [attributeFilterValues, setAttributeFilterValues] = useState<Record<number, { min?: number; max?: number; value?: string; dateMin?: string; dateMax?: string }>>({});
  const [selectedBrand, setSelectedBrand] = useState<number | undefined>();
  const [selectedModel, setSelectedModel] = useState<number | undefined>();
  const [yearFrom, setYearFrom] = useState<number | undefined>();
  const [yearTo, setYearTo] = useState<number | undefined>();
  const [brands, setBrands] = useState<BrandDto[]>([]);
  const [models, setModels] = useState<ModelDto[]>([]);
  const [compatibilityDrawerOpen, setCompatibilityDrawerOpen] = useState(false);

  // Ayarlar
  const [includeWholesale, setIncludeWholesale] = useState(true);
  const { baseCurrencySymbol } = useCurrency();

  const buildAttributeFilters = (): AttributeFilterParam[] => {
    const result: AttributeFilterParam[] = [];
    for (const attr of categoryAttributeFilters) {
      const v = attributeFilterValues[attr.id];
      if (!v) continue;
      if (attr.dataType === AttributeDataType.Number && (v.min != null || v.max != null)) {
        result.push({ categoryAttributeId: attr.id, valueMin: v.min, valueMax: v.max });
      } else if (attr.dataType === AttributeDataType.Text && v.value != null && v.value.trim() !== '') {
        result.push({ categoryAttributeId: attr.id, value: v.value.trim() });
      } else if (attr.dataType === AttributeDataType.Date && (v.dateMin || v.dateMax)) {
        result.push({ categoryAttributeId: attr.id, valueDateMin: v.dateMin, valueDateMax: v.dateMax });
      }
    }
    return result;
  };

  const getSortedCategoriesHierarchically = (cats: Category[]): Array<Category & { level: number; fullName: string }> => {
    const result: Array<Category & { level: number; fullName: string }> = [];
    const addCategoryAndChildren = (parentId: number | null | undefined, level: number = 0) => {
      const children = cats
        .filter(c => c.parentId === parentId)
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
      children.forEach(cat => {
        let fullName = cat.categoryName;
        let currentCat = cat;
        while (currentCat.parentId) {
          const parent = cats.find(c => c.id === currentCat.parentId);
          if (!parent) break;
          fullName = `${parent.categoryName} > ${fullName}`;
          currentCat = parent;
        }
        result.push({ ...cat, level, fullName });
        addCategoryAndChildren(cat.id, level + 1);
      });
    };
    addCategoryAndChildren(null, 0);
    addCategoryAndChildren(undefined, 0);
    return result;
  };

  useEffect(() => {
    loadCategories();
    loadProductBrands();
    loadBrands();
    loadFromLocalStorage();
  }, []);

  const loadProductBrands = async () => {
    try {
      const data = await fetchProductBrands();
      setProductBrands(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ürün markaları yüklenirken hata:', error);
    }
  };

  const loadBrands = async () => {
    try {
      const data = await fetchBrands();
      setBrands(data);
    } catch (error) {
      console.error('Markalar yüklenirken hata:', error);
    }
  };

  const loadModels = async () => {
    if (selectedBrand) {
      try {
        const data = await fetchModels(selectedBrand);
        setModels(data);
      } catch (error) {
        console.error('Modeller yüklenirken hata:', error);
      }
    } else {
      setModels([]);
      setSelectedModel(undefined);
    }
  };

  useEffect(() => {
    loadModels();
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedCategory) {
      setCategoryAttributeFilters([]);
      setAttributeFilterValues({});
      return;
    }
    (async () => {
      try {
        const attrs = await fetchCategoryAttributes(selectedCategory);
        setCategoryAttributeFilters(attrs.filter(a => a.isFilterable));
        setAttributeFilterValues({});
      } catch {
        setCategoryAttributeFilters([]);
      }
    })();
  }, [selectedCategory]);

  useEffect(() => {
    saveToLocalStorage();
  }, [selectedProducts]);

  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const attrFilters = buildAttributeFilters();
      const result = await fetchProducts(
        1,
        1000,
        selectedCategory ?? undefined,
        searchText.trim() || undefined,
        undefined,
        selectedProductBrand,
        selectedBrand,
        selectedModel,
        yearFrom,
        yearTo,
        attrFilters.length > 0 ? attrFilters : undefined
      );
      const productsData = Array.isArray(result.data) ? result.data : [];

      const productsWithVariants = await Promise.all(
        productsData.map(async (product: any) => {
          try {
            const variantsData = await fetchProductVariants(product.id);
            if (variantsData && variantsData.length > 0) {
              return variantsData.map((variant: any) => {
                const variantCombination = variant.values
                  ?.map((v: any) => `${v.variantName}: ${v.variantValue}`)
                  .join(', ') || '';
                return {
                  id: variant.id,
                  productId: product.id,
                  productCode: variant.sku || variant.productCode,
                  productName: variantCombination
                    ? `${product.productName} - ${variantCombination}`
                    : product.productName,
                  sku: variant.sku,
                  barcode: variant.primaryBarcode,
                  categoryId: product.categoryId,
                  categoryName: product.categoryName,
                  productBrandId: product.productBrandId,
                  isVariant: true,
                  variantCombination,
                  currentRetailPrice: variant.price || 0,
                  currentWholesalePrice: variant.wholesalePrice || 0,
                  currentPurchasePrice: variant.purchasePrice || 0
                };
              });
            }
            return [{
              id: product.id,
              productCode: product.productCode,
              productName: product.productName,
              barcode: product.barcode,
              categoryId: product.categoryId,
              categoryName: product.categoryName,
              productBrandId: product.productBrandId,
              isVariant: false,
              currentRetailPrice: product.retailPrice ?? 0,
              currentWholesalePrice: product.wholesalePrice ?? 0,
              currentPurchasePrice: product.purchasePrice ?? 0
            }];
          } catch (error) {
            console.error(`Ürün ${product.id} varyantları alınamadı:`, error);
            return [{
              id: product.id,
              productCode: product.productCode,
              productName: product.productName,
              barcode: product.barcode,
              categoryId: product.categoryId,
              categoryName: product.categoryName,
              productBrandId: product.productBrandId,
              isVariant: false,
              currentRetailPrice: 0,
              currentWholesalePrice: 0,
              currentPurchasePrice: 0
            }];
          }
        })
      );
      const flatProducts = productsWithVariants.flat();
      setProducts(flatProducts);
    } catch (error: any) {
      message.error('Ürünler yüklenemedi');
      console.error('Ürünler yüklenirken hata:', error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, searchText, selectedProductBrand, selectedBrand, selectedModel, yearFrom, yearTo, attributeFilterValues, categoryAttributeFilters]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const loadCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (error) {
      console.error('Kategoriler yüklenemedi:', error);
    }
  };

  const loadFromLocalStorage = () => {
    const saved = localStorage.getItem('priceBulkUpdate');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Dayjs nesnelerine dönüştür
        const converted = parsed.map((item: any) => ({
          ...item,
          validFrom: dayjs(item.validFrom),
          validTo: item.validTo ? dayjs(item.validTo) : undefined
        }));
        setSelectedProducts(converted);
      } catch (error) {
        console.error('LocalStorage yükleme hatası:', error);
      }
    }
  };

  const saveToLocalStorage = () => {
    localStorage.setItem('priceBulkUpdate', JSON.stringify(selectedProducts));
  };

  const handleAddProduct = (product: Product) => {
    console.log('Eklenen ürün:', product);
    
    const exists = selectedProducts.find(p => 
      product.isVariant 
        ? (p.productVariantId === product.id)
        : (p.id === product.id && !p.isVariant)
    );
    if (exists) {
      message.warning('Bu ürün zaten listede');
      return;
    }

    // Fiyat bilgileri zaten product'ta var
    const newProduct: SelectedProduct = {
      id: product.isVariant ? product.id : product.id,
      productId: product.isVariant ? product.productId : product.id,
      productVariantId: product.isVariant ? product.id : undefined,
      productCode: product.productCode,
      productName: product.productName,
      purchasePrice: product.currentPurchasePrice || 0,
      currentRetailPrice: product.currentRetailPrice || 0,
      currentWholesalePrice: product.currentWholesalePrice || 0,
      validFrom: dayjs(),
      isIndefinite: true,
      isVariant: product.isVariant
    };

    console.log('Eklenecek ürün:', newProduct);
    setSelectedProducts([...selectedProducts, newProduct]);
    message.success(`${product.productName} eklendi`);
  };

  const handleRemoveProduct = (productId: number) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const handlePriceChange = (productId: number, field: 'newRetailPrice' | 'newWholesalePrice', value: number | null) => {
    setSelectedProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, [field]: value || undefined } : p
    ));
  };

  const handleDateChange = (productId: number, field: 'validFrom' | 'validTo', value: Dayjs | null) => {
    setSelectedProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, [field]: value || undefined } : p
    ));
  };

  const handleIndefiniteChange = (productId: number, checked: boolean) => {
    setSelectedProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, isIndefinite: checked, validTo: checked ? undefined : p.validTo } : p
    ));
  };

  const validateProducts = () => {
    for (const product of selectedProducts) {
      if (!product.newRetailPrice) {
        message.error(`${product.productName} için perakende fiyat girilmedi`);
        return false;
      }
      if (includeWholesale && !product.newWholesalePrice) {
        message.error(`${product.productName} için toptan fiyat girilmedi`);
        return false;
      }
      if (!product.validFrom) {
        message.error(`${product.productName} için başlangıç tarihi girilmedi`);
        return false;
      }
      if (!product.isIndefinite && !product.validTo) {
        message.error(`${product.productName} için bitiş tarihi girilmedi`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (selectedProducts.length === 0) {
      message.warning('Lütfen en az bir ürün ekleyin');
      return;
    }

    if (!validateProducts()) {
      return;
    }

    try {
      setLoading(true);
      
      // Her ürün için fiyat kaydetme
      for (const product of selectedProducts) {
        // Perakende fiyat
        await api.post('/productprices', {
          productId: product.productId,
          productVariantId: product.productVariantId || null,
          priceTypeId: 1, // Perakende
          price: product.newRetailPrice,
          validFrom: product.validFrom.toISOString(),
          validTo: product.isIndefinite ? null : product.validTo?.toISOString(),
          isIndefinite: product.isIndefinite
        });

        // Toptan fiyat
        if (includeWholesale && product.newWholesalePrice) {
          await api.post('/productprices', {
            productId: product.productId,
            productVariantId: product.productVariantId || null,
            priceTypeId: 2, // Toptan
            price: product.newWholesalePrice,
            validFrom: product.validFrom.toISOString(),
            validTo: product.isIndefinite ? null : product.validTo?.toISOString(),
            isIndefinite: product.isIndefinite
          });
        }
      }

      message.success(`${selectedProducts.length} ürün için fiyatlar güncellendi`);
      setSelectedProducts([]);
      localStorage.removeItem('priceBulkUpdate');
      
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Fiyatlar kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  // Filtrelenmiş ürünler (API'de arama/kategori/marka/atribut/araç uygulanıyor; sadece barkod client-side)
  const filteredProducts = products.filter((product: any) => {
    if (searchBarcode && !product.barcode?.includes(searchBarcode)) return false;
    return true;
  });

  const categoriesWithHierarchy = getSortedCategoriesHierarchically(categories);

  const handleSearch = () => {
    loadProducts();
  };

  const leftColumns = [
    {
      title: 'Ürün',
      key: 'product',
      width: 200,
      render: (_: any, record: Product) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#1890ff' }}>
            {record.productCode}
          </span>
          <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            {record.productName || '-'}
          </span>
        </div>
      )},
    {
      title: 'Perakende',
      dataIndex: 'currentRetailPrice',
      key: 'currentRetailPrice',
      width: 100,
      render: (price: number) => price ? `${baseCurrencySymbol}${price.toFixed(2)}` : `${baseCurrencySymbol}0.00`},
    {
      title: 'Alış',
      dataIndex: 'currentPurchasePrice',
      key: 'currentPurchasePrice',
      width: 100,
      render: (price: number) => price ? `${baseCurrencySymbol}${price.toFixed(2)}` : `${baseCurrencySymbol}0.00`},
    ...(includeWholesale ? [{
      title: 'Toptan',
      dataIndex: 'currentWholesalePrice',
      key: 'currentWholesalePrice',
      width: 100,
      render: (price: number) => price ? `${baseCurrencySymbol}${price.toFixed(2)}` : `${baseCurrencySymbol}0.00`}] : []),
    {
      title: 'İşlem',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: Product) => {
        const isAdded = selectedProducts.some(p => 
          record.isVariant 
            ? (p.productVariantId === record.id)
            : (p.id === record.id && !p.isVariant)
        );
        
        return (
          <Tooltip title="Listeye ekle">
            <Button
              type="primary"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => handleAddProduct(record)}
              disabled={isAdded}
            />
          </Tooltip>
        );
      }},
  ];

  const rightColumns = [
    {
      title: 'Ürün',
      key: 'product',
      width: 180,
      render: (_: any, record: SelectedProduct) => (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontWeight: 'bold', fontSize: '12px', color: '#1890ff' }}>
            {record.productCode || '-'}
          </span>
          <span style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
            {record.productName || '-'}
          </span>
        </div>
      )},
    {
      title: 'Alış Fiyatı',
      dataIndex: 'purchasePrice',
      key: 'purchasePrice',
      width: 100,
      render: (price: number) => `${baseCurrencySymbol}${price.toFixed(2)}`},
    {
      title: 'Perakende Fiyat',
      key: 'retailPrice',
      width: 90,
      render: (_: any, record: SelectedProduct) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <InputNumber
            size="small"
            min={0}
            value={record.newRetailPrice}
            onChange={(value) => handlePriceChange(record.id, 'newRetailPrice', value)}
            style={{ width: '100%' }}
            prefix={baseCurrencySymbol}
            placeholder="Yeni fiyat"
            status={!record.newRetailPrice ? 'error' : ''}
          />
          <span style={{ fontSize: '11px', color: '#999' }}>
            Son: {baseCurrencySymbol}{record.currentRetailPrice.toFixed(2)}
          </span>
        </Space>
      )},
    ...(includeWholesale ? [{
      title: 'Toptan Fiyat',
      key: 'wholesalePrice',
      width: 90,
      render: (_: any, record: SelectedProduct) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <InputNumber
            size="small"
            min={0}
            value={record.newWholesalePrice}
            onChange={(value) => handlePriceChange(record.id, 'newWholesalePrice', value)}
            style={{ width: '100%' }}
            prefix={baseCurrencySymbol}
            placeholder="Yeni fiyat"
            status={!record.newWholesalePrice ? 'error' : ''}
          />
          <span style={{ fontSize: '11px', color: '#999' }}>
            Son: {baseCurrencySymbol}{record.currentWholesalePrice.toFixed(2)}
          </span>
        </Space>
      )}] : []),
    {
      title: 'Geçerlilik',
      key: 'validity',
      width: 300,
      render: (_: any, record: SelectedProduct) => (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space size="small">
            <DatePicker
              size="small"
              value={record.validFrom}
              onChange={(value) => handleDateChange(record.id, 'validFrom', value)}
              format="DD/MM/YYYY"
              placeholder="Başlangıç"
              style={{ width: 130 }}
              status={!record.validFrom ? 'error' : ''}
            />
            {!record.isIndefinite && (
              <DatePicker
                size="small"
                value={record.validTo}
                onChange={(value) => handleDateChange(record.id, 'validTo', value)}
                format="DD/MM/YYYY"
                placeholder="Bitiş"
                style={{ width: 130 }}
                status={!record.isIndefinite && !record.validTo ? 'error' : ''}
              />
            )}
          </Space>
          <Checkbox
            checked={record.isIndefinite}
            onChange={(e) => handleIndefiniteChange(record.id, e.target.checked)}
          >
            Süresiz
          </Checkbox>
        </Space>
      )},
    {
      title: 'İşlem',
      key: 'action',
      width: 80,
      render: (_: any, record: SelectedProduct) => (
        <Popconfirm
          title="Listeden çıkar?"
          onConfirm={() => handleRemoveProduct(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
          />
        </Popconfirm>
      )},
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card 
        title="Fiyat Güncelleme Sihirbazı"
        extra={
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSave}
            loading={loading}
            size="large"
          >
            Kaydet ({selectedProducts.length})
          </Button>
        }
      >
        <Row gutter={16}>
          {/* SOL TARAF - Ürün Listesi */}
          <Col span={12}>
            <Card
              title="Ürünler"
              size="small"
              extra={
                <Space>
                  <span>Toptan Fiyatları Güncelle:</span>
                  <Switch
                    checked={includeWholesale}
                    onChange={setIncludeWholesale}
                    checkedChildren="Açık"
                    unCheckedChildren="Kapalı"
                  />
                </Space>
              }
            >
              <Space wrap style={{ marginBottom: 12 }}>
                <Input
                  placeholder="Ürün adı veya kodu ara..."
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                  style={{ width: 220 }}
                  allowClear
                />
                <Input
                  placeholder="Barkod ara..."
                  prefix={<SearchOutlined />}
                  value={searchBarcode}
                  onChange={(e) => setSearchBarcode(e.target.value)}
                  style={{ width: 180 }}
                  allowClear
                />
                <Select
                  placeholder="Kategori seçiniz"
                  value={selectedCategory ?? undefined}
                  onChange={(value) => setSelectedCategory(value ?? null)}
                  style={{ width: 200 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const categoryName = categoriesWithHierarchy.find(c => c.id === option?.value)?.categoryName || '';
                    return categoryName.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {categoriesWithHierarchy.map(category => (
                    <Select.Option key={category.id} value={category.id}>
                      <span style={{ paddingLeft: `${category.level * 20}px` }}>
                        {category.level > 0 && '└─ '}
                        {category.categoryName}
                      </span>
                    </Select.Option>
                  ))}
                </Select>
                <Select
                  placeholder="Ürün markası seçiniz"
                  value={selectedProductBrand}
                  onChange={(value) => setSelectedProductBrand(value)}
                  style={{ width: 180 }}
                  allowClear
                  showSearch
                  filterOption={(input, option) => {
                    const name = productBrands.find(b => b.id === option?.value)?.name || '';
                    return name.toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {productBrands
                    .filter(b => b.isActive)
                    .sort((a, b) => {
                      if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                      return a.name.localeCompare(b.name);
                    })
                    .map(pb => (
                      <Select.Option key={pb.id} value={pb.id}>
                        {pb.isFavourite && '⭐ '}
                        {pb.name}
                      </Select.Option>
                    ))}
                </Select>
                {categoryAttributeFilters.map(attr => {
                  const v = attributeFilterValues[attr.id] ?? {};
                  const label = attr.unit ? `${attr.name} (${attr.unit})` : attr.name;
                  if (attr.dataType === AttributeDataType.Number) {
                    return (
                      <Space key={attr.id} size={4}>
                        <InputNumber
                          placeholder={`${label} min`}
                          value={v.min}
                          onChange={min => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], min: min ?? undefined } }))}
                          style={{ width: 120 }}
                        />
                        <InputNumber
                          placeholder={`${label} max`}
                          value={v.max}
                          onChange={max => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], max: max ?? undefined } }))}
                          style={{ width: 120 }}
                        />
                      </Space>
                    );
                  }
                  if (attr.dataType === AttributeDataType.Text) {
                    return (
                      <Input
                        key={attr.id}
                        placeholder={label}
                        value={v.value ?? ''}
                        onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], value: e.target.value } }))}
                        style={{ width: 120 }}
                      />
                    );
                  }
                  if (attr.dataType === AttributeDataType.Date) {
                    return (
                      <Space key={attr.id} size={4}>
                        <Input
                          type="date"
                          placeholder={`${label} başlangıç`}
                          value={v.dateMin ?? ''}
                          onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], dateMin: e.target.value || undefined } }))}
                          style={{ width: 130 }}
                        />
                        <Input
                          type="date"
                          placeholder={`${label} bitiş`}
                          value={v.dateMax ?? ''}
                          onChange={e => setAttributeFilterValues(prev => ({ ...prev, [attr.id]: { ...prev[attr.id], dateMax: e.target.value || undefined } }))}
                          style={{ width: 130 }}
                        />
                      </Space>
                    );
                  }
                  return null;
                })}
                <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                  Ara
                </Button>
                <Button
                  icon={<CarOutlined />}
                  onClick={() => setCompatibilityDrawerOpen(true)}
                >
                  Araç uyumluluk
                  {(selectedBrand || selectedModel != null || yearFrom != null || yearTo != null) && (
                    <span style={{ marginLeft: 4, opacity: 0.85 }}>(filtre aktif)</span>
                  )}
                </Button>
              </Space>
              <Drawer
                title={<><CarOutlined /> Araç uyumluluk</>}
                placement="right"
                open={compatibilityDrawerOpen}
                onClose={() => setCompatibilityDrawerOpen(false)}
                width={360}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Select
                    placeholder="Marka seçiniz"
                    value={selectedBrand}
                    onChange={(value) => {
                      setSelectedBrand(value);
                      setSelectedModel(undefined);
                    }}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    filterOption={(input, option) => {
                      const brandName = brands.find(b => b.id === option?.value)?.name || '';
                      return brandName.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {brands
                      .filter(b => b.isActive)
                      .sort((a, b) => {
                        if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(brand => (
                        <Select.Option key={brand.id} value={brand.id}>
                          {brand.isFavourite && '⭐ '}
                          {brand.name}
                        </Select.Option>
                      ))}
                  </Select>
                  <Select
                    placeholder="Model seçiniz"
                    value={selectedModel}
                    onChange={(value) => setSelectedModel(value)}
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    disabled={!selectedBrand}
                    filterOption={(input, option) => {
                      const modelName = models.find(m => m.id === option?.value)?.name || '';
                      return modelName.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {models
                      .filter(m => m.isActive)
                      .sort((a, b) => {
                        if (a.isFavourite !== b.isFavourite) return b.isFavourite ? 1 : -1;
                        return a.name.localeCompare(b.name);
                      })
                      .map(model => (
                        <Select.Option key={model.id} value={model.id}>
                          {model.isFavourite && '⭐ '}
                          {model.displayName}
                        </Select.Option>
                      ))}
                  </Select>
                  <InputNumber
                    placeholder="Yıl (Başlangıç)"
                    value={yearFrom}
                    onChange={(value) => setYearFrom(value ? value : undefined)}
                    style={{ width: '100%' }}
                    min={1900}
                    max={2100}
                  />
                  <InputNumber
                    placeholder="Yıl (Bitiş)"
                    value={yearTo}
                    onChange={(value) => setYearTo(value ? value : undefined)}
                    style={{ width: '100%' }}
                    min={1900}
                    max={2100}
                  />
                  <Button type="primary" block onClick={() => setCompatibilityDrawerOpen(false)}>
                    Tamam
                  </Button>
                </Space>
              </Drawer>
              <Table
                dataSource={filteredProducts}
                columns={leftColumns}
                rowKey="id"
                size="small"
                pagination={{ pageSize: 10 }}
                loading={loading}
                scroll={{ y: 500, x: 'max-content' }}
              />
            </Card>
          </Col>

          {/* SAĞ TARAF - Seçili Ürünler */}
          <Col span={12}>
            <Card 
              title={`Fiyatı Güncellenecek Ürünler (${selectedProducts.length})`}
              size="small"
            >
              <Table
                dataSource={selectedProducts}
                columns={rightColumns}
                rowKey="id"
                size="small"
                pagination={false}
                scroll={{ y: 500, x: 'max-content' }}
                locale={{
                  emptyText: 'Sol taraftan ürün ekleyin'
                }}
              />
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default PriceBulkUpdate;

